import { JumpsGame } from '../../games/jumps/JumpsGame.js';
import { JumpsVortex } from '../../games/jumps/JumpsVortex.js';
import { JumpsRenderer } from '../../games/jumps/JumpsRenderer.js';
import { Grid } from '../../core/Grid.js';
import { Position, positionKey } from '../../core/GridGeometry.js';
import { RenderConfig } from '../../rendering/Renderer.js';
import { TutorialGameAdapter } from '../TutorialEngine.js';

// ── Jumps-specific types ─────────────────────────────────────────

export interface JumpsRenderOptions {
    // Placeholder — JumpsRenderer is fully game-state driven.
}

// ── Base Adapter ─────────────────────────────────────────────────

/**
 * Drives a real JumpsGame from a scene string. The string is the game's own serialized
 * form, optionally followed by `+click1,click2,…` — cells fed through `handleClick`, so a
 * scene can animate the actual build FSM (start a ramp, extend it, click the end to launch)
 * exactly as a player would, rather than hand-authoring intermediate serialized states.
 */
export class JumpsTutorialAdapter implements TutorialGameAdapter {
    private game: JumpsGame;
    private renderer: JumpsRenderer;
    private grid: Grid;

    constructor(canvas: HTMLCanvasElement, grid: Grid) {
        this.grid = grid;
        this.game = new JumpsGame(grid);
        this.renderer = new JumpsRenderer(canvas, grid);
        // Static scenes: no tweening, so the renderer never self-re-renders and
        // wipes the engine-drawn annotations.
        this.renderer.setSelfAnimation(false);
    }

    applyState(state: unknown): void {
        const raw = state as string;
        this.game.reset();
        if (!raw) return;

        const plusIndex = raw.indexOf('+');
        const rampPart = plusIndex >= 0 ? raw.substring(0, plusIndex) : raw;
        const clickPart = plusIndex >= 0 ? raw.substring(plusIndex + 1) : '';

        if (rampPart) {
            this.game.deserialize(rampPart);
        }
        if (clickPart) {
            for (const posStr of clickPart.split(',')) {
                const trimmed = posStr.trim();
                if (!trimmed) continue;
                this.game.handleClick(this.grid.serializer.deserializePosition(trimmed));
            }
        }
    }

    render(_options: unknown): void {
        this.renderer.render(this.game);
    }

    getConfig(): RenderConfig {
        return this.renderer.getConfig();
    }
}

export function createJumpsTutorialAdapter(
    canvas: HTMLCanvasElement,
    grid: Grid,
): TutorialGameAdapter {
    return new JumpsTutorialAdapter(canvas, grid);
}

// ── Vortex state ─────────────────────────────────────────────────

export interface JumpsVortexTutorialState {
    ramps: string;
    gateMode?: boolean;
    /**
     * Extra gate pairs (cell strings, e.g. ['B5', 'D5']) merged on top of the adapter's
     * default pair for this scene only — used to show a second, blocked (amber) pair. Omit
     * otherwise.
     */
    extraGates?: Array<[string, string]>;
}

/** Parse a cell string like "B5" into a Position ({ row: 5, col: 1 }). */
function parseCellString(s: string): Position {
    return { col: s.charCodeAt(0) - 65, row: parseInt(s.slice(1), 10) };
}

// ── Vortex Adapter ───────────────────────────────────────────────

export class JumpsVortexTutorialAdapter implements TutorialGameAdapter {
    private game: JumpsVortex;
    private renderer: JumpsRenderer;
    private gatePairs: Array<[Position, Position]>;
    private currentGateMode: boolean = false;

    constructor(
        canvas: HTMLCanvasElement,
        grid: Grid,
        gatePairs: Array<[Position, Position]>,
    ) {
        this.game = new JumpsVortex(grid);
        this.renderer = new JumpsRenderer(canvas, grid);
        this.renderer.setSelfAnimation(false);   // static scenes — see the Base adapter
        this.gatePairs = gatePairs;
    }

    applyState(state: unknown): void {
        const s = state as JumpsVortexTutorialState | null;
        this.game.reset();

        const pairs = [...this.gatePairs];
        for (const [a, b] of s?.extraGates ?? []) {
            pairs.push([parseCellString(a), parseCellString(b)]);
        }
        const allGates = new Set<string>();
        for (const [a, b] of pairs) {
            allGates.add(positionKey(a));
            allGates.add(positionKey(b));
        }
        this.game.setVortexData(pairs, allGates);

        if (s?.ramps) {
            this.game.deserialize(s.ramps);
        }
        this.currentGateMode = s?.gateMode ?? false;
    }

    render(_options: unknown): void {
        this.renderer.setGateMode(this.currentGateMode);
        this.renderer.render(this.game);
    }

    getConfig(): RenderConfig {
        return this.renderer.getConfig();
    }
}

export function createJumpsVortexTutorialAdapter(
    canvas: HTMLCanvasElement,
    grid: Grid,
): TutorialGameAdapter {
    // Gate pair for the vortex Jumps tutorial: SW body cell C3 ↔ SE body cell E4, so a ramp
    // can be built through the gate to launch from the far quarter (Jérémy's reference).
    const gatePairs: Array<[Position, Position]> = [
        [{ row: 3, col: 2 }, { row: 4, col: 4 }],
    ];
    return new JumpsVortexTutorialAdapter(canvas, grid, gatePairs);
}
